var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var _a;
import { createSelector } from "@reduxjs/toolkit";
import { NodeComponentType, TubeType, } from "./types.current";
import { getMissingGripPositionComponent, isGripPositionUnconfigured, selectSystemDataRootDefineParameters, } from "store/system-data/define-parameters/selectors";
import { getVacuumPumpComponent, isCentralizedSystem, } from "store/system-data/helpers";
import { computeGripFrame } from "3d/grip-frame";
import { DEFAULT_LAYER, LAYERS } from "store/layers";
var ALLOCATED_NODE_IDS = (_a = {},
    _a[NodeComponentType.GRIP_POSITION] = 3,
    _a[NodeComponentType.CENTRALIZED_VACUUM_PUMP] = 1,
    _a[NodeComponentType.FEED_PRESSURE_POINT] = 1,
    _a[NodeComponentType.TUBE_CONNECTION] = 2,
    _a);
var MAX_ID = 257;
var MAX_CENTRALIZED_PUMPS_COUNT = 4;
export var selectSceneRoot = function (state) { return state.scene.present; };
export var selectNodes = function (state) { return selectSceneRoot(state).nodes; };
export var selectEdges = function (state) { return selectSceneRoot(state).edges; };
export var selectAttachments = function (state) {
    return selectSceneRoot(state).attachments;
};
export var selectObjectShape = function (state) {
    return selectSceneRoot(state).objectShape;
};
export var selectObjectSize = function (state) {
    return selectSceneRoot(state).objectSize;
};
export var selectCardboardBoxes = function (state) {
    return selectSceneRoot(state).cardboardBoxes;
};
export var selectPallet = function (state) { return selectSceneRoot(state).pallet; };
export var selectLayerPatterns = function (state) {
    return selectSceneRoot(state).layerPatterns;
};
export var selectSavedPalletLayoutIds = function (state) {
    return selectSceneRoot(state).savedPalletLayoutIds;
};
/*
 * Whether this system works with a pallet of boxes at all. The whole multi-box
 * surface — the pallet, the palletizing presets, the stacking layers and the
 * per-box controls — is only offered while this is on; with it off, a cardboard
 * system looks like every other application.
 */
export var selectMultipleBoxesEnabled = function (state) {
    return selectSceneRoot(state).multipleBoxesEnabled;
};
export var selectCanUndo = function (state) { return state.scene.past.length > 0; };
export var selectCanRedo = function (state) {
    return state.scene.future.length > 0;
};
export var selectNode = function (nodeId) {
    return createSelector(selectNodes, function (nodes) { return (nodeId ? nodes[nodeId] : undefined); });
};
export var selectEdge = function (edgeId) {
    return createSelector(selectEdges, function (edges) { return (edgeId ? edges[edgeId] : undefined); });
};
export var selectAttachment = function (attachmentId) {
    return createSelector(selectAttachments, function (attachments) {
        return attachmentId ? attachments[attachmentId] : undefined;
    });
};
export var selectFeedPressurePoints = createSelector(selectNodes, function (nodes) {
    return Object.values(nodes).filter(function (node) { return node.type === NodeComponentType.FEED_PRESSURE_POINT; });
});
export var selectCentralizedPumps = createSelector(selectNodes, function (nodes) {
    return Object.values(nodes).filter(function (node) { return node.type === NodeComponentType.CENTRALIZED_VACUUM_PUMP; });
});
export var selectTubeConnections = createSelector(selectNodes, function (nodes) {
    return Object.values(nodes).filter(function (node) { return node.type === NodeComponentType.TUBE_CONNECTION; });
});
/*
 * Tube connections with no connection type picked yet: the placeholders "Add
 * tube connection" puts in the scene for the user to pick a type for. Like an
 * unpicked pump (`selectCentralPumpNodesWithoutPump`), a placeholder is nothing
 * but such a node — drawn as an icon, with no tube attachments, and left out
 * of the calculation and the summary.
 */
export var selectTubeConnectionPlaceholders = createSelector(selectTubeConnections, function (tubeConnections) {
    return tubeConnections.filter(function (node) { return node.component.type === undefined; });
});
export var selectGripPositions = createSelector(selectNodes, function (nodes) {
    return Object.values(nodes).filter(function (node) { return node.type === NodeComponentType.GRIP_POSITION; });
});
/*
 * The stacking layers this system actually uses. A system is single-layer
 * until the user adds the second layer (`addSecondLayer`); the second layer is
 * in use as soon as it holds boxes or has a pattern recorded for it. Nothing
 * forces two layers: the layer switch, the warnings and the per-layer
 * calculations all follow this list, so a single-layer system pays for exactly
 * one calculation.
 */
export var selectLayersInUse = createSelector(selectCardboardBoxes, selectLayerPatterns, function (cardboardBoxes, layerPatterns) {
    return cardboardBoxes.some(function (box) { return box.layer === 1; }) || layerPatterns[1]
        ? __spreadArray([], __read(LAYERS), false) : [DEFAULT_LAYER];
});
export var selectIsSecondLayerInUse = createSelector(selectLayersInUse, function (layers) { return layers.length > 1; });
/*
 * The frame ("grimma") the suction cups are mounted on, derived from where the
 * cups currently are — see `3d/grip-frame`. `null` when there are too few cups
 * for a frame to mean anything (a single cup already moves on its own). A
 * `null` frame is also what makes the frame unpickable, so the drag goes away
 * with the geometry.
 */
export var selectGripFrame = createSelector(selectNodes, computeGripFrame);
/** Whether the frame stands turned a quarter turn (see `turnGripFrame`). */
export var selectIsGripFrameTurned = function (state) {
    return Boolean(selectSceneRoot(state).gripFrameTurned);
};
export var allowAddCentralizedPump = createSelector(selectNodes, function (nodes) {
    var vacuumPump = Object.values(nodes).filter(function (node) { return node.type === NodeComponentType.CENTRALIZED_VACUUM_PUMP; });
    return vacuumPump.length < MAX_CENTRALIZED_PUMPS_COUNT;
});
export var selectEdgeConnections = function (edgeId) {
    return createSelector(selectEdges, selectAttachments, selectNodes, function (edges, attachments, nodes) {
        /* An edge can briefly reference an attachment that no longer exists
         * (e.g. while attachments are being rebuilt). Resolve defensively and
         * surface the result as possibly-undefined so callers handle it. */
        var edge = edges[edgeId];
        var fromAttachment = edge
            ? attachments[edge.fromAttachmentId]
            : undefined;
        var toAttachment = edge
            ? attachments[edge.toAttachmentId]
            : undefined;
        return {
            fromAttachment: fromAttachment,
            toAttachment: toAttachment,
            fromNode: fromAttachment ? nodes[fromAttachment.nodeId] : undefined,
            toNode: toAttachment ? nodes[toAttachment.nodeId] : undefined,
        };
    });
};
export var selectAvailableIdsCount = createSelector(selectNodes, selectEdges, function (nodes, edges) {
    var nodeIds = Object.values(nodes).reduce(function (total, currentNode) {
        return total + ALLOCATED_NODE_IDS[currentNode.type];
    }, 0);
    var edgeIds = Object.values(edges).reduce(function (total, currentEdge) {
        return (total +
            //Compressed air tubes may be double
            (currentEdge.tubeType === TubeType.COMPRESSED_AIR ? 2 : 1));
    }, 0);
    return MAX_ID - (nodeIds + edgeIds);
});
/* How many more grip positions the node id budget has room for. */
export var selectAvailableGripPositionIdCount = createSelector(selectAvailableIdsCount, function (availableIdsCount) {
    return Math.max(0, Math.floor(availableIdsCount / ALLOCATED_NODE_IDS[NodeComponentType.GRIP_POSITION]));
});
export var selectHasAvailableIdsForGripPosition = createSelector(selectAvailableGripPositionIdCount, function (count) { return count >= 1; });
export var selectHasAvailableIdsForTubeConnection = createSelector(selectAvailableIdsCount, function (availableIdsCount) {
    return availableIdsCount >= ALLOCATED_NODE_IDS[NodeComponentType.TUBE_CONNECTION];
});
export var selectHasAvailableIdsForPump = createSelector(selectAvailableIdsCount, function (availableIdsCount) {
    return availableIdsCount >=
        ALLOCATED_NODE_IDS[NodeComponentType.CENTRALIZED_VACUUM_PUMP];
});
/* Grip positions missing a component required for a calculation: a cup, and
 * in a decentralized system also a pump. */
export var selectEmptyGripPositionIds = createSelector(selectGripPositions, selectSystemDataRootDefineParameters, function (gripPositions, systemDataDefineParameters) {
    return gripPositions
        .filter(function (node) {
        return getMissingGripPositionComponent(node.id, systemDataDefineParameters) !== undefined;
    })
        .map(function (node) { return node.id; });
});
export var selectHasGripPositionsWithMissingComponents = createSelector(selectEmptyGripPositionIds, function (emptyGripPositionIds) { return emptyGripPositionIds.length > 0; });
/* Grip positions where nothing at all has been picked — the only ones that are
 * safe to remove on the user's behalf (see `isGripPositionUnconfigured`). */
export var selectUnconfiguredGripPositionIds = createSelector(selectGripPositions, selectSystemDataRootDefineParameters, function (gripPositions, systemDataDefineParameters) {
    return gripPositions
        .filter(function (node) {
        return isGripPositionUnconfigured(node.id, systemDataDefineParameters);
    })
        .map(function (node) { return node.id; });
});
/**
 * The distinct pumps selected on nodes in the scene that don't support energy
 * saving: decentral pumps on grip positions in a decentralized system, central
 * pumps otherwise. Only pumps mapped to a scene node count — a pump left in an
 * unmapped Tacton selection slot isn't part of the system the user can see.
 */
export var selectPumpsWithoutEnergySaving = createSelector(selectGripPositions, selectCentralizedPumps, selectSystemDataRootDefineParameters, function (gripPositions, centralizedPumps, systemDataDefineParameters) {
    var e_1, _a;
    var systemData = systemDataDefineParameters.dataState.data;
    if (!systemData)
        return [];
    var pumpNodes = isCentralizedSystem(systemData)
        ? centralizedPumps
        : gripPositions;
    var pumpsByValue = new Map();
    try {
        for (var pumpNodes_1 = __values(pumpNodes), pumpNodes_1_1 = pumpNodes_1.next(); !pumpNodes_1_1.done; pumpNodes_1_1 = pumpNodes_1.next()) {
            var node = pumpNodes_1_1.value;
            var pump = getVacuumPumpComponent(node.id, systemDataDefineParameters.pumpSelectionCentralMap, systemDataDefineParameters.pumpSelectionDecentralMap, systemData);
            if (pump && !pump.isEnergySavingAvailable) {
                pumpsByValue.set(pump.value, pump);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (pumpNodes_1_1 && !pumpNodes_1_1.done && (_a = pumpNodes_1.return)) _a.call(pumpNodes_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return __spreadArray([], __read(pumpsByValue.values()), false);
});
